import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type UIEvent,
} from "react";

import { useSelectContext } from "../SelectContext";

/** Distance from the bottom of the list, in px, at which `onLoadMore` fires. */
const LOAD_MORE_THRESHOLD = 100;

type LoadMoreState = { phase: "idle" } | { phase: "requested"; itemCount: number } | { phase: "loading" };

export interface UseSelectContentArgs {
  searchThreshold: number;
  searchable: boolean | undefined;
  filter: "internal" | "external";
  search: string | undefined;
  onSearchChange: ((search: string) => void) | undefined;
  loading: boolean;
  onLoadMore: (() => void) | undefined;
  hasMore: boolean;
}

export const useSelectContent = ({
  searchThreshold,
  searchable,
  filter,
  search,
  onSearchChange,
  loading,
  onLoadMore,
  hasMore,
}: UseSelectContentArgs) => {
  const { multiple, value, itemCount, modal, creator, setOpen, suppressFocusOpen, closedBySelection } =
    useSelectContext();
  const [uncontrolledSearch, setUncontrolledSearch] = useState("");
  const currentSearch = search ?? uncontrolledSearch;
  const inputRef = useRef<HTMLInputElement>(null);
  const commandNode = useRef<HTMLDivElement | null>(null);
  const interactedOutside = useRef(false);
  const closedByEscape = useRef(false);
  const pendingCommit = useRef<(() => void) | null>(null);

  // Typing is how a `creator` makes an option, so it always shows the input. Externally filtered
  // results can shrink below the threshold mid-search; the input must not vanish under the cursor.
  const showSearch = !!creator || (searchable ?? (filter === "external" || itemCount > searchThreshold));

  const setSearch = useCallback(
    (nextSearch: string) => {
      setUncontrolledSearch(nextSearch);
      onSearchChange?.(nextSearch);
    },
    [onSearchChange]
  );

  const handleOpenAutoFocus = (event: Event) => {
    event.preventDefault();
    interactedOutside.current = false;
    closedByEscape.current = false;
    pendingCommit.current = null;
    (inputRef.current ?? commandNode.current)?.focus();
  };

  const handleInteractOutside = () => {
    interactedOutside.current = true;
  };

  const handleEscapeKeyDown = (event: KeyboardEvent, onEscapeKeyDown?: (event: KeyboardEvent) => void) => {
    onEscapeKeyDown?.(event);
    if (!event.defaultPrevented) closedByEscape.current = true;
  };

  // Radix returns focus to the trigger on close unless the user interacted outside a non-modal
  // popover. When it will, flag it so the trigger's `openOnFocus` doesn't reopen immediately.
  // Escape is the cancel gesture and a selection already committed, so only other closes (click
  // outside, tabbing away) commit the creator row's typed text under `commitOnClose`.
  const handleCloseAutoFocus = () => {
    if (modal || !interactedOutside.current) suppressFocusOpen.current = true;
    if (!closedByEscape.current && !closedBySelection.current) pendingCommit.current?.();
    pendingCommit.current = null;
    if (currentSearch) setSearch("");
  };

  // The dropdown is portaled to the end of the document, so Tab would otherwise leave focus nowhere;
  // like a native select, it closes the dropdown and focus returns to the trigger.
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab" || event.defaultPrevented) return;
    event.preventDefault();
    setOpen(false);
  };

  // A next page extends the list, so its items stay up; any other load (a new search) replaces them,
  // and stale results would read as the answer. Only this component knows which load it asked for.
  // The consumer's `loading` may turn on a render after the request, so "requested" waits for it — or,
  // for a consumer that appends without ever loading, for the items to change.
  const [loadMoreState, setLoadMoreState] = useState<LoadMoreState>({ phase: "idle" });
  if (loadMoreState.phase === "requested" && loading) setLoadMoreState({ phase: "loading" });
  if (loadMoreState.phase === "requested" && !loading && itemCount !== loadMoreState.itemCount)
    setLoadMoreState({ phase: "idle" });
  if (loadMoreState.phase === "loading" && !loading) setLoadMoreState({ phase: "idle" });

  // Several scroll events can arrive before the render that records a request; this blocks them until it commits.
  const requestedSinceCommit = useRef(false);
  useLayoutEffect(() => {
    requestedSinceCommit.current = false;
  });

  const requestLoadMore = () => {
    if (!onLoadMore || !hasMore || loading || loadMoreState.phase !== "idle" || requestedSinceCommit.current) return;
    requestedSinceCommit.current = true;
    setLoadMoreState({ phase: "requested", itemCount });
    onLoadMore();
  };

  // A list too short to scroll never fires the scroll that asks for more — e.g. a page the consumer
  // filtered down to a few items. Re-measured after every render, since any render can change its contents.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = listRef.current;
    if (list && list.scrollHeight - list.clientHeight < LOAD_MORE_THRESHOLD) requestLoadMore();
  });

  const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD) requestLoadMore();
  };

  const showItems = !loading || loadMoreState.phase !== "idle";

  // The collector hands off to the list when the content actually mounts, not on `open`: Radix's
  // Portal mounts the content a render after `open` flips (leaving no registered copy as auto-focus
  // runs), and keeps it mounted through the exit animation (overlapping both copies throughout).
  // Tracked on the cmdk root because it takes this ref as-is; cmdk's List composes its ref anew
  // every render, which would re-run this callback and loop.
  const [contentMounted, setContentMounted] = useState(false);
  const commandRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    commandNode.current = node;
    setContentMounted(true);
    return () => {
      commandNode.current = null;
      setContentMounted(false);
    };
  }, []);
  // A request whose consumer neither loaded nor added items would otherwise block every later one.
  if (!contentMounted && loadMoreState.phase === "requested") setLoadMoreState({ phase: "idle" });

  return {
    highlightedOnOpen: multiple ? undefined : value ?? undefined,
    showSearch,
    showItems,
    collectItems: !contentMounted || !showItems,
    currentSearch,
    setSearch,
    pendingCommit,
    inputRef,
    commandRef,
    listRef,
    handleOpenAutoFocus,
    handleInteractOutside,
    handleEscapeKeyDown,
    handleCloseAutoFocus,
    handleKeyDown,
    handleListScroll,
  };
};
